import { Injectable } from '@angular/core';

import { Store } from '@ngxs/store';
import { Observable, Subscriber } from 'rxjs';
import { map, switchMap, takeWhile, tap } from 'rxjs/operators';

import { ReferralsWebsocketService } from '../referrals-websocket.service';

enum ImportEventType {
  Progress = 'job:pending',
  Finished = 'job:finished',
}

interface ImportEventPayload {
  uuid: string;
  count: number;
  total: number;
  failure: number;
  skipped: number;
}

interface ImportProgressEvent {
  progress: number;
  failCount: number;
  successCount: number;
  totalCount: number;
  done: boolean;
}

export class ConnectionsImportProgress {
  public static type: string = '[Websocket] Import file progress';
  public name: ImportEventType.Progress = ImportEventType.Progress;
  constructor(public payload: ImportEventPayload) {}
}

export class ConnectionsImportFinished {
  public static type: string = '[Websocket] Import file finished';
  public name: ImportEventType.Finished = ImportEventType.Finished;
  constructor(public payload: ImportEventPayload) {}
}

@Injectable({ providedIn: 'root' })
export class ReferralsImportSocketService {
  private readonly eventClassMap: Record<
    ImportEventType,
    typeof ConnectionsImportProgress | typeof ConnectionsImportFinished
  > = {
    [ImportEventType.Progress]: ConnectionsImportProgress,
    [ImportEventType.Finished]: ConnectionsImportFinished,
  };

  constructor(private websocketService: ReferralsWebsocketService, private store: Store) {}

  public calcProgress(
    event: ConnectionsImportProgress | ConnectionsImportFinished,
  ): {
    progress: number;
    failCount: number;
    successCount: number;
    totalCount: number;
    done: boolean;
  } {
    const { count, total, failure, skipped } = event.payload;
    const processedEntries = count + failure + skipped;
    return {
      progress: Math.round((processedEntries / total) * 100),
      failCount: failure,
      successCount: processedEntries - failure,
      totalCount: total,
      done: event instanceof ConnectionsImportFinished,
    };
  }

  public getImportProgress(importId: number): Observable<ImportProgressEvent> {
    const channel$ = this.websocketService.subscribeToChannel(`connections-import:${importId}`);

    return this.websocketService.ready$.pipe(
      switchMap(() => channel$),
      switchMap(
        channel =>
          new Observable<ConnectionsImportProgress | ConnectionsImportFinished>(
            subscriber => (channel.onMessage = this.onImportMessage(subscriber)),
          ),
      ),
      takeWhile(importEvent => ImportEventType.Progress === importEvent.name, true),
      tap(importEvent => this.store.dispatch(importEvent)),
      map(importEvent => this.calcProgress(importEvent)),
    );
  }

  private onImportMessage(
    subscriber: Subscriber<ConnectionsImportProgress | ConnectionsImportFinished>,
  ): <_T, P>(eventName: ImportEventType, payload: any) => P {
    return (eventName: ImportEventType, payload: any) => {
      if (!eventName || !eventName.startsWith('job:')) return payload;

      const importEvent = new this.eventClassMap[eventName](payload);
      if (importEvent) {
        subscriber.next(importEvent);
      } else {
        console.warn(`Unknown event: ${eventName}`);
      }

      return payload;
    };
  }
}
